import React, { useEffect } from 'react';
import { Container, Button, Row, Col, Carousel } from 'react-bootstrap';
import { 
  FaWhatsapp, FaCheck, FaRuler, FaTruck, FaChartLine, 
  FaUsers, FaCompressArrowsAlt, FaTimes, FaShieldAlt, 
  FaGoogle, FaStar 
} from 'react-icons/fa';
import { Helmet } from 'react-helmet';
import './Suelos.css';

// --------------------------------------------------------
// 1. COMPONENTES PUROS (Afuera de la función principal)
// --------------------------------------------------------
const mainPhone = "542215739000";

const ContactButton = ({ text = "HABLA CON UN ASESOR", variant = "danger", className = "" }) => (
  <Button 
    href={`https://wa.me/${mainPhone}?text=${encodeURIComponent("Hola Darom SA, estoy interesado en sus servicios de suelos industriales. ¿Podrían brindarme más información sobre sus soluciones DRM 360™?")}`}  
    target="_blank"
    rel="noopener noreferrer"
    className={`darom-cta-button ${variant} darom-track-button fw-bold ${className}`}
  >
    <FaWhatsapp className="me-2" size={20} /> {text}
  </Button>
);

// Pastilla de Google Maps estandarizada con el resto de la web
const GoogleRatingBadge = () => (
  <div className="d-flex justify-content-center w-100 mt-4 animate__animated animate__fadeInUp">
    <a 
      href="#reviews" 
      className="d-inline-flex align-items-center text-decoration-none py-2 px-4 shadow-lg bg-white rounded-pill" 
      style={{ transition: 'transform 0.2s', width: 'auto', whiteSpace: 'nowrap' }}
      onMouseOver={(e) => e.currentTarget.style.transform = 'scale(1.05)'}
      onMouseOut={(e) => e.currentTarget.style.transform = 'scale(1)'}
    >
      <FaGoogle className="text-danger me-2" size={20} />
      <div className="d-flex align-items-center mx-2 border-end border-secondary border-opacity-25 pe-3">
        <span className="fw-bold fs-5 me-1" style={{ color: '#212529' }}>4.7</span>
        <FaStar className="text-warning" size={18} />
      </div>
      <span className="small ms-2 fw-bold" style={{ color: '#495057' }}>50+ Reseñas Verificadas</span>
    </a>
  </div>
);

// --------------------------------------------------------
// 2. DATOS ESTÁTICOS
// --------------------------------------------------------
const suppliers = [
  { src: '/logolomanegra.png', alt: 'Loma Negra' },
  { src: '/LOGOSIKA.png', alt: 'Sika' },
  { src: '/logoacindarofi.png', alt: 'Red Acindar' },
  { src: '/logodafre.jpg', alt: 'Dafre' },
  { src: '/logo varsovia.jpg', alt: 'Grupo Varsovia' },
  { src: '/LOGOTECMA.PNG', alt: 'Tecma' },
  { src: '/logoeleprint.png', alt: 'Eleprint' },
  { src: '/logogoldir.png', alt: 'Goldir' },
  { src: '/dycasalogo.jpg', alt: 'Ieb Construcciones' },
  { src: '/logoaubasa.png', alt: 'Aubasa' },
  { src: '/logopfisterer.png', alt: 'Pfisterer' },
  { src: '/logoweber.png', alt: 'Weber' },
  { src: '/logofanelli.png', alt: 'Fanelli' },
  { src: '/logoctibor.png', alt: 'Ctibor' }
];

const testimonios = [
  { text: "Los mejores! Compré todo el hierro, ladrillos y materiales para mi casa. Llené la platea y 3 losas de hormigón con ellos, todo impecable. Destaco la atención, productos y servicio de entrega.", author: "Pablo Laurito" },
  { text: "Muy buena atención. Eficacia, rapidez y puntualidad en la entrega. Excelente logística para satisfacer las necesidades de sus clientes.", author: "Sebastian Godoy" },
  { text: "Excelente servicio de atención, entregas sin demoras y seguimiento de pedidos. Respuesta inmediata y asesoramiento técnico especializado.", author: "Grupo Varsovia" },
  { text: "Cumplieron con los tiempos y lo pactado. Destaco la excelente predisposición y programación impecable de Guillermo.", author: "Laura Scandroglio" },
  { text: "La atención y comunicación son impecables. Gran predisposición para resolver problemas técnicos. Altamente recomendable.", author: "Arq. De los Santos" },
  { text: "Excelente atención y predisposición para asesorar en materiales de construcción.", author: "Matias Muchenik" },
  { text: "Resalto la atención de Aixa: profesional, predispuesta y resolutiva. Muy satisfecho con el servicio.", author: "Manuel Garriga" },
  { text: "Excelente servicio. Comunicación clara y logística eficiente para obras de gran envergadura.", author: "Claudio Castiglia" },
  { text: "Los mejores precios y servicio en 20 años. Siempre que puedo, elijo trabajar con ellos por su seriedad.", author: "Emmanuel Bor" },
  { text: "Atención rápida y soluciones a medida para proyectos arquitectónicos complejos.", author: "Goldir" },
  { text: "Equipo confiable y detallista. Materiales de primera calidad para estructuras exigentes.", author: "German Luiso" }
];

const fasesEstudio = [
  { title: "Reconocimiento del terreno", text: "Inspección visual y recolección de datos iniciales" },
  { title: "Perforaciones y muestreo", text: "Extracción de muestras a diferentes profundidades" },
  { title: "Análisis de laboratorio", text: "Ensayo de humedad, granulometría y resistencia" },
  { title: "Informe técnico", text: "Documentación detallada con recomendaciones" }
];

const equiposMovimiento = [
  { name: "Excavadora Hidráulica", spec: "Cuchara de 1.2 m³ (ideal para zanjas y cimentaciones)", img: "/portadamov6.jpg" },
  { name: "Bulldozer de Alto Rendimiento", spec: "Motor 350 HP (para desplome de terrenos difíciles)", img: "/portadamov4.jpg" },
  { name: "Compactadora Vibratoria", spec: "Fuerza de compactación: 13 toneladas (suelos estables en menos tiempo)", img: "/portadamovofi.jpg" }
];

const garantiasData = [
  { icon: <FaRuler />, title: "Medición Exacta", text: "Precisión de ±1mm con escáner láser 3D" },
  { icon: <FaTruck />, title: "Entrega Rápida", text: "Resultados en 48 horas (garantizado)" },
  { icon: <FaCompressArrowsAlt />, title: "Suelos Compactos", text: "Compactación certificada ≥95% (norma ASTM D698)" },
  { icon: <FaShieldAlt />, title: "Cumplimiento Legal", text: "100% conforme a regulaciones municipales y nacionales" },
  { icon: <FaChartLine />, title: "Análisis Profesional", text: "Informes con estadísticas y gráficos comprensibles" },
  { icon: <FaUsers />, title: "Asesoría Personalizada", text: "Soporte técnico especializado durante y después del estudio" }
];

// --------------------------------------------------------
// 3. COMPONENTE PRINCIPAL
// --------------------------------------------------------
function Suelos() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="darom-body">
      <Helmet>
        <title>Suelos Industriales | Estudio y Movimiento Profesional | Darom SA</title>
        <meta name="description" content="Paquete SUELO DRM: estudio geotécnico completo y movimiento de suelos con garantía de compactación. Optimiza tus proyectos desde la base." />
        <meta name="keywords" content="estudio de suelos, movimiento de tierras, compactación, cimentación, geotecnia, construcción, Darom SA" />
        <meta property="og:title" content="Suelos Industriales | Darom SA" />
        <meta property="og:description" content="Soluciones profesionales para estudio y movimiento de suelos con garantía de calidad." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://daromsa.com.ar/services/suelos" />
        <meta property="og:image" content="/portadamov1.jpg" />
        <link rel="canonical" href="https://daromsa.com.ar/services/suelos" />
        
        {/* BLOQUE SEO JSON-LD */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Service",
            "name": "Estudio y Movimiento de Suelos",
            "provider": {
              "@type": "Organization",
              "name": "Darom SA",
              "url": "https://daromsa.com.ar"
            },
            "description": "Estudio geotécnico completo y movimiento de suelos con garantía de compactación en la Zona Sur del Gran Buenos Aires.",
            "serviceType": "Construction",
            "areaServed": "Buenos Aires - Zona Sur"
          })}
        </script>
      </Helmet>
      
      {/* --- Sección Hero Centrada Mejorada --- */}
      <section className="darom-hero-section position-relative">
        {/* Overlay sutil para mejor legibilidad */}
        <div className="position-absolute w-100 h-100" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }} />
        
        {/* Imagen de fondo original */}
        <div className="darom-hero-image position-absolute w-100 h-100" style={{ 
          backgroundImage: `url('/portadamov1.jpg')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }} />
        
        {/* Contenido principal */}
        <div className="darom-hero-overlay position-relative" style={{zIndex: 2}}>
          <Container>
            <Row className="justify-content-center">
              <Col lg={9} className="text-center">
                <h1 className="text-white display-3 fw-bold mb-3 animate__animated animate__fadeIn">
                  <span className="d-block">TE AYUDAMOS A CONSTRUIR</span>
                  <span className="text-danger">LA BASE PERFECTA</span> PARA TU PROYECTO
                </h1>
                
                <p className="text-white lead fs-2 mb-4" style={{textShadow: '0 2px 4px rgba(0,0,0,0.3)'}}>
                  <strong>PAQUETE SUELO DRM</strong> - Cimentaciones seguras con respaldo técnico certificado
                </p>
                
                <div 
                  className="benefits-container p-4 rounded-3 mb-4 mx-auto" 
                  style={{
                    maxWidth: '700px',
                    backgroundColor: 'rgba(0, 0, 0, 0.15)',
                    backdropFilter: 'blur(4px)',
                    border: '1px solid rgba(255, 255, 255, 0.1)'
                  }}
                >
                  <ul className="list-unstyled fs-5 row g-3 g-md-4 text-start">
                    <li className="col-md-6 d-flex align-items-start text-white">
                      <FaCheck className="text-danger mt-1 me-2 flex-shrink-0" />
                      <span>Estudio geotécnico completo con informes normativos</span>
                    </li>
                    <li className="col-md-6 d-flex align-items-start text-white">
                      <FaCheck className="text-danger mt-1 me-2 flex-shrink-0" />
                      <span>Movimiento de suelos optimizado para tu proyecto</span>
                    </li>
                    <li className="col-md-6 d-flex align-items-start text-white">
                      <FaCheck className="text-danger mt-1 me-2 flex-shrink-0" />
                      <span>Compactación certificada con equipos de última generación</span>
                    </li>
                    <li className="col-md-6 d-flex align-items-start text-white">
                      <FaCheck className="text-danger mt-1 me-2 flex-shrink-0" />
                      <span>Solución integral desde el análisis hasta la ejecución</span>
                    </li>
                  </ul>
                </div>
                
                <div className="d-flex justify-content-center mb-4">
                  <ContactButton className="btn-lg px-4 py-3 fw-bold shadow-lg" style={{fontSize: '1.2rem'}} />
                </div>
                
                {/* Componente Google Maps */}
                <GoogleRatingBadge />
              </Col>
            </Row>
          </Container>
        </div>
      </section>

      {/* --- Prueba Social --- */}
      <section className="py-3 bg-light">
        <Container>
          <h2 className="text-center mb-2">EMPRESAS QUE <span className="text-red">CONFÍAN EN NOSOTROS</span></h2>
          
          <div className="suppliers-carousel-container">
            {/* Versión Desktop */}
            <div className="d-none d-md-block">
              <Carousel indicators={false} interval={3000} pause="hover" touch={true} variant="dark">
                {Array.from({ length: Math.ceil(suppliers.length / 6) }).map((_, slideIndex) => (
                  <Carousel.Item key={`desktop-${slideIndex}`}>
                    <Row className="justify-content-center g-4">
                      {suppliers.slice(slideIndex * 6, (slideIndex + 1) * 6).map((supplier, idx) => (
                        <Col key={`desktop-${slideIndex}-${idx}`} md={2} className="text-center">
                          <div className="supplier-card bg-white p-2 rounded shadow-sm">
                            <img src={supplier.src} alt={supplier.alt} className="supplier-logo img-fluid" loading="lazy" style={{maxHeight: '60px', objectFit: 'contain'}} />
                          </div>
                        </Col>
                      ))}
                    </Row>
                  </Carousel.Item>
                ))}
              </Carousel>
            </div>

            {/* Versión Mobile */}
            <div className="d-block d-md-none">
              <Carousel indicators={false} interval={3000} pause="hover" touch={true} variant="dark">
                {Array.from({ length: Math.ceil(suppliers.length / 3) }).map((_, slideIndex) => (
                  <Carousel.Item key={`mobile-${slideIndex}`}>
                    <Row className="justify-content-center g-3">
                      {suppliers.slice(slideIndex * 3, (slideIndex + 1) * 3).map((supplier, idx) => (
                        <Col key={`mobile-${slideIndex}-${idx}`} xs={4} className="text-center">
                          <div className="supplier-card bg-white p-2 rounded shadow-sm">
                            <img src={supplier.src} alt={supplier.alt} className="supplier-logo img-fluid" loading="lazy" style={{maxHeight: '40px', objectFit: 'contain'}} />
                          </div>
                        </Col>
                      ))}
                    </Row>
                  </Carousel.Item>
                ))}
              </Carousel>
            </div>
          </div>
        </Container>
      </section>

      {/* --- Sección Problemas --- */}
      <section className="darom-section darom-section-dark">
        <Container>
          <h2 className="darom-section-title">RIESGOS DE NO HACER UN CORRECTO ESTUDIO DE SUELO</h2>
          <Row className="g-4 my-2">
            {[
              "Grietas en estructuras por asentamientos",
              "Inundaciones por napas freáticas no detectadas",
              "Sobre costos por cimentaciones mal diseñadas",
              "Rechazo de permisos municipales"
            ].map((problema, idx) => (
              <Col md={3} sm={6} key={idx}>
                <div className="darom-problem-card text-center h-100 p-3 bg-dark border border-danger rounded shadow">
                  <FaTimes className="text-danger mb-3" size={30} />
                  <p className="mb-0 text-white">{problema}</p>
                </div>
              </Col>
            ))}
          </Row>
          <div className="text-center mt-4">
            <ContactButton text="EVITA ESTOS PROBLEMAS" />
          </div>
        </Container>
      </section>

      {/* --- Sección Garantías --- */}
      <section className="darom-section darom-section-light py-5 bg-white">
        <Container>
          <h2 className="darom-section-title text-center mb-5">NUESTRAS <span className="text-danger">GARANTÍAS</span></h2>
          <Row className="g-4">
            {garantiasData.map((item, index) => (
              <Col md={4} key={index}>
                <div className="darom-card darom-card-danger text-center h-100 p-4 shadow-sm border rounded bg-light">
                  <div className="darom-card-icon text-danger mb-3" style={{ fontSize: '2.5rem' }}>{item.icon}</div>
                  <h4 className="fw-bold">{item.title}</h4>
                  <p className="text-muted mb-0">{item.text}</p>
                </div>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* --- Sección Estudio de Suelos --- */}
      <section className="darom-section darom-section-gray py-5 bg-light">
        <Container>
          <Row className="align-items-center">
            <Col md={6} className="mb-4 mb-md-0">
              <h2 className="fw-bold mb-4">ESTUDIO DE SUELOS <span className="text-danger">CERTIFICADO</span></h2>
              <p className="lead">Nuestros informes cumplen con los requisitos de:</p>
              <ul className="mb-4 fs-5 list-unstyled">
                <li className="mb-2"><FaCheck className="text-danger me-2" />Normas IRAM 2541-2542</li>
                <li><FaCheck className="text-danger me-2" />Reglamentos municipales</li>
              </ul>
              <p className="mb-4 fw-bold">Resultados confiables para tomar las mejores decisiones en tu proyecto.</p>
              <ContactButton />
              <p className="mt-2 small text-muted fw-bold">Asesoramiento sin cargo</p>
            </Col>
            <Col md={6}>
              <img src="/estudiosuelo.jpg" alt="Equipo de estudio" className="img-fluid rounded shadow-lg" loading="lazy" />
            </Col>
          </Row>
        </Container>
      </section>

      {/* --- Fases del Estudio --- */}
      <section className="darom-section darom-section-light py-5">
        <Container>
          <h2 className="darom-section-title text-center mb-5">ETAPAS DEL <span className="text-danger">ESTUDIO GEOTÉCNICO</span></h2>
          <div className="darom-timeline row g-4">
            {fasesEstudio.map((fase, idx) => (
              <Col md={3} key={idx} className="text-center">
                <div className="bg-danger text-white rounded-circle d-inline-flex justify-content-center align-items-center mb-3 shadow" style={{ width: '60px', height: '60px' }}>
                  <FaCheck size={24} />
                </div>
                <h4 className="fw-bold">{fase.title}</h4>
                <p className="text-muted">{fase.text}</p>
              </Col>
            ))}
          </div>
          <div className="text-center mt-5">
            <ContactButton text="CONOCE NUESTRO MÉTODO" />
          </div>
        </Container>
      </section>

      {/* --- Sección Movimiento de Suelos --- */}
      <section className="darom-section darom-section-dark py-5 bg-dark text-white">
        <Container>
          <Row className="align-items-center">
            <Col md={6} className="order-md-2 mb-4 mb-md-0 ps-md-5">
              <h2 className="fw-bold mb-4">MOVIMIENTO DE SUELOS <span className="text-danger">PRECISO</span></h2>
              <p className="lead">Con maquinaria de última generación para:</p>
              <ul className="mb-4 fs-5 list-unstyled">
                <li className="mb-2"><FaCheck className="text-danger me-2" />Excavaciones profundas</li>
                <li className="mb-2"><FaCheck className="text-danger me-2" />Nivelación láser</li>
                <li className="mb-2"><FaCheck className="text-danger me-2" />Compactación certificada</li>
                <li><FaCheck className="text-danger me-2" />Manejo de materiales especiales</li>
              </ul>
              <ContactButton variant="danger" text="COTIZAR MOVIMIENTO" />
            </Col>
            <Col md={6} className="order-md-1">
              <img src="/portadamov5.jpg" alt="Maquinaria para movimiento de suelos" className="img-fluid rounded shadow-lg border border-secondary" loading="lazy" />
            </Col>
          </Row>
        </Container>
      </section>

      {/* --- Equipos --- */}
      <section className="darom-section darom-section-gray py-5 bg-light">
        <Container>
          <h2 className="darom-section-title text-center mb-5">NUESTRA <span className="text-danger">FLOTA</span></h2>
          <Row className="g-4">
            {equiposMovimiento.map((equipo, idx) => (
              <Col md={4} key={idx}>
                <div className="darom-card darom-card-danger text-center bg-white p-3 rounded shadow-sm h-100">
                  <img src={equipo.img} alt={equipo.name} className="img-fluid mb-3 rounded shadow-sm w-100" style={{ height: '200px', objectFit: 'cover' }} loading="lazy" />
                  <h4 className="fw-bold text-dark">{equipo.name}</h4>
                  <p className="text-muted mb-0">{equipo.spec}</p>
                </div>
              </Col>
            ))}
          </Row>
          <div className="text-center mt-5">
            <ContactButton text="HABLA CON UN ASESOR" />
          </div>
        </Container>
      </section>

      {/* --- Testimonios --- */}
      <section id="reviews" className="darom-section darom-section-light py-5 border-top">
        <Container>
          <h2 className="darom-section-title text-center mb-5">CLIENTES <span className="text-danger">SATISFECHOS</span></h2>
          
          <div className="d-none d-md-block">
            <Carousel indicators={false} interval={5000} variant="dark">
              {[...Array(Math.ceil(testimonios.length / 3))].map((_, i) => (
                <Carousel.Item key={`d-${i}`}>
                  <Row className="g-4 pb-4">
                    {testimonios.slice(i * 3, i * 3 + 3).map((testimonio, idx) => (
                      <Col md={4} key={`desktop-${i}-${idx}`}>
                        <div className="darom-testimonial-card h-100 p-4 text-center bg-white border rounded shadow-sm">
                          <div className="text-warning mb-3">
                            <FaStar/><FaStar/><FaStar/><FaStar/><FaStar/>
                          </div>
                          <p className="fst-italic text-muted mb-4">"{testimonio.text}"</p>
                          <p className="fw-bold text-dark mb-0">— {testimonio.author}</p>
                        </div>
                      </Col>
                    ))}
                  </Row>
                </Carousel.Item>
              ))}
            </Carousel>
          </div>

          <div className="d-block d-md-none">
            <Carousel indicators={true} interval={4000} variant="dark">
              {testimonios.map((testimonio, idx) => (
                <Carousel.Item key={`m-${idx}`}>
                  <div className="darom-testimonial-card mx-auto p-4 text-center bg-white border rounded shadow-sm mb-5" style={{maxWidth: '90%'}}>
                    <div className="text-warning mb-3">
                      <FaStar/><FaStar/><FaStar/><FaStar/><FaStar/>
                    </div>
                    <p className="fst-italic text-muted mb-4">"{testimonio.text}"</p>
                    <p className="fw-bold text-dark mb-0">— {testimonio.author}</p>
                  </div>
                </Carousel.Item>
              ))}
            </Carousel>
          </div>
        </Container>
      </section>

      {/* --- Paquetes --- */}
      <section className="darom-section darom-section-light py-5 bg-light border-top">
        <Container>
          <h2 className="darom-section-title text-center mb-5">PAQUETES <span className="text-danger">SUELOS</span></h2>
          <Row className="g-4">
            
            <Col lg={4}>
              <div className="darom-package-card bg-white border p-4 rounded shadow-sm h-100 d-flex flex-column">
                <h3 className="fw-bold text-dark mb-3">1. Estudio Básico SUELODRM™</h3>
                <p className="fw-bold text-danger">Informe geotécnico para obras chicas</p>
                <ul className="mb-4 flex-grow-1 list-unstyled text-muted">
                  <li className="mb-2"><FaCheck className="text-success me-2" />3 perforaciones estándar (hasta 5m)</li>
                  <li className="mb-2"><FaCheck className="text-success me-2" />Ensayos de humedad y granulometría</li>
                  <li className="mb-2"><FaCheck className="text-success me-2" />Recomendaciones de cimentación</li>
                </ul>
                <div className="mt-auto">
                  <p className="fw-bold fs-6 text-dark bg-light p-2 rounded text-center">Ideal para: Viviendas unifamiliares</p>
                  <ContactButton text="COTIZAR ESTE PAQUETE" className="w-100" />
                </div>
              </div>
            </Col>

            <Col lg={4}>
              <div className="darom-package-card bg-white border border-danger p-4 rounded shadow-lg h-100 d-flex flex-column position-relative">
                <div className="position-absolute top-0 start-50 translate-middle badge bg-danger rounded-pill px-3 py-2">
                  MÁS ELEGIDO
                </div>
                <h3 className="fw-bold text-dark mb-3 mt-2">2. Estudio INDUSTRIALDRM™</h3>
                <p className="fw-bold text-danger">Análisis exhaustivo para proyectos</p>
                <ul className="mb-4 flex-grow-1 list-unstyled text-muted">
                  <li className="mb-2"><FaCheck className="text-success me-2" />6 perforaciones (hasta 10m)</li>
                  <li className="mb-2"><FaCheck className="text-success me-2" />Ensayos de resistencia</li>
                  <li className="mb-2"><FaCheck className="text-success me-2" />Detección de napas freáticas</li>
                  <li className="mb-2"><FaCheck className="text-success me-2" />Informe normativas SENASA</li>
                </ul>
                <div className="mt-auto">
                  <p className="fw-bold fs-6 text-dark bg-light p-2 rounded text-center">Ideal para: Naves industriales</p>
                  <ContactButton text="COTIZAR ESTE PAQUETE" className="w-100" />
                </div>
              </div>
            </Col>

            <Col lg={4}>
              <div className="darom-package-card bg-dark text-white border p-4 rounded shadow-sm h-100 d-flex flex-column">
                <h3 className="fw-bold mb-3">3. SOLUCIÓN SUELO360™</h3>
                <p className="fw-bold text-warning">Estudio + Movimiento de suelos</p>
                <ul className="mb-4 flex-grow-1 list-unstyled opacity-75">
                  <li className="mb-2"><FaCheck className="text-warning me-2" />Todo lo del Paquete 2</li>
                  <li className="mb-2"><FaCheck className="text-warning me-2" />Movimiento y compactación</li>
                  <li className="mb-2"><FaCheck className="text-warning me-2" />Certificado final de densidad</li>
                  <li className="mb-2"><FaCheck className="text-warning me-2" />1 año de garantía estructural</li>
                </ul>
                <div className="mt-auto">
                  <p className="fw-bold fs-6 bg-white bg-opacity-10 p-2 rounded text-center text-warning">Ahorro del 15% incluido</p>
                  <ContactButton text="COTIZAR ESTE PAQUETE" className="w-100" />
                </div>
              </div>
            </Col>

          </Row>
        </Container>
      </section>

      {/* --- CTA Final --- */}
      <section className="darom-section bg-danger text-white py-5">
        <Container className="text-center">
          <h2 className="mb-3 fw-bold display-6">¿LISTO PARA COMENZAR TU PROYECTO?</h2>
          <p className="lead mb-4 fs-4">Nuestros especialistas están disponibles para asesorarte hoy mismo.</p>
          <div className="d-flex justify-content-center">
            <Button 
              href={`https://wa.me/${mainPhone}?text=${encodeURIComponent("Hola Darom SA, necesito un presupuesto de sus servicios")}`}
              target="_blank"
              rel="noopener noreferrer"
              variant="dark"
              size="lg"
              className="fw-bold px-5 py-3 shadow-sm d-flex align-items-center"
            >
              <FaWhatsapp className="me-2" size={24} /> CONTACTAR AHORA
            </Button>
          </div>
        </Container>
      </section>
    </div>
  );
}

export default Suelos;